import { Dictionary } from '@reduxjs/toolkit';
import { keyBy, reverse, sortBy } from 'lodash';
import {
  InferredProgressValue,
  PastProjectStatus,
  ProgressStatus,
  Project,
  ProjectFinancialsContractType,
} from 'api/project';
import { CustomRevenueRecognitionTypes } from 'api/settings-financials/types';
import { StatusGroup } from 'api/tasks';
import { minsToHours } from 'components/organisms/financials/utils/common';
import { FilterableField } from 'components/organisms/tasks/fields';
import { byCreatedAt } from 'utils/comparators/by-created-at';
import { byStartDate } from 'utils/comparators/by-start-date';

type FieldLookupById = Dictionary<FilterableField>;

export const getFieldMapReducer =
  (fieldLookup: FieldLookupById) =>
  (
    hash: Record<string, unknown>,
    {
      fieldValue,
      fieldId,
    }: { fieldId: number; fieldValue: unknown; isEditable?: boolean },
  ) => {
    const field = fieldLookup[fieldId];

    if (field) {
      hash[field.fieldName] = fieldValue;
    }

    return hash;
  };

export const calculateProgressPercent = (
  progressStatus?: ProgressStatus[],
  totalTaskCount = 0,
) => {
  const progressLookup = keyBy(progressStatus ?? [], 'group');
  const completed = progressLookup[StatusGroup.Completed]?.count ?? 0;
  const total = totalTaskCount ?? 0;
  let progress = completed / total;

  progress = Number.isNaN(progress) ? 0 : progress;

  return Math.round(progress * 100);
};

export const lastStatusUpdatedAt = (statuses: PastProjectStatus[]) =>
  reverse(sortBy(statuses, 'updatedAt'))[0]?.updatedAt;

export type ProjectListItem = Omit<
  Project,
  | 'fields'
  | 'projectOwner'
  | 'currentPhases'
  | 'customer'
  | 'partners'
  | 'teamMembers'
  | 'inferredProgress'
> &
  Record<string, unknown> & {
    progressPercent: number;
    meta: Pick<Project, 'customer'>;
  } & { rowId?: string };

type TransformFunction = (
  fieldLookup: FieldLookupById,
  isPaginationEnabled?: boolean,
  groupKey?: string | null,
) => (data: Project[]) => ProjectListItem[];

export const transform: TransformFunction =
  (fieldLookup, isPaginationEnabled = false, groupKey = null) =>
  (data) => {
    const initial: ProjectListItem[] = [];
    const toFieldMap = getFieldMapReducer(fieldLookup);

    let projectData: Project[] = data;

    if (isPaginationEnabled) {
      projectData = data;
    } else {
      projectData = data.sort(byCreatedAt).reverse();
    }

    return projectData.reduce(
      (
        list,
        { fields, customer, teamMembers, inferredProgress, partners, ...rest },
      ) => {
        const fieldsMap = (fields ?? []).reduce(toFieldMap, {});
        const { currentPhases = [] } = rest;
        const currentPhasesValue = currentPhases
          .sort(byStartDate)
          .reverse()
          .map(({ projectPhaseName }) => projectPhaseName);

        const progressPercent = calculateProgressPercent(
          rest.progressStatus,
          rest.totalTaskCount,
        );

        const meta = {
          customer,
        };

        list.push({
          ...rest,
          ...fieldsMap,
          rowId:
            groupKey != null
              ? `${groupKey}_${rest.projectId}`
              : rest.projectId.toString(),
          currentPhases:
            currentPhasesValue.length > 0 ? currentPhasesValue : null,
          progressPercent,
          meta,
          arr: rest.annualizedRecurringRevenue,
          customer: customer.companyId,
          partnerCompany: partners?.map(({ companyId }) => companyId),
          projectOwner: rest.projectOwner?.userId,
          inferredProgress:
            inferredProgress?.value === InferredProgressValue.None
              ? undefined
              : inferredProgress?.value,
          estimatedDueDate: rest.inferredProgressDays,
          teamMembers: (teamMembers ?? []).map(({ userId }) => userId),
          projectMinutes: {
            budget_hours:
              rest.financialContractType ===
              ProjectFinancialsContractType.SUBSCRIPTION
                ? minsToHours(
                    (rest.projectFinancialsDetailModel
                      ?.projectFinancialsMetaData?.periodMinutes ?? 0) *
                      (rest.currentPeriodData?.numberOfPeriods ?? 1),
                  )
                : rest.budgetedHours,
            budgetMinutes:
              rest.financialContractType ===
              ProjectFinancialsContractType.SUBSCRIPTION
                ? (rest.projectFinancialsDetailModel?.projectFinancialsMetaData
                    ?.periodMinutes ?? 0) *
                  (rest.currentPeriodData?.numberOfPeriods ?? 1)
                : rest.budgetedMinutes,
            total_minutes: rest.totalMinutes,
            project_name: rest.projectName,
            billableMinutes: (rest?.metrics?.billableHours ?? 0) * 60,
          },
          billableMinutes: (rest?.metrics?.billableHours ?? 0) * 60,
          lastStatusUpdate: lastStatusUpdatedAt(rest.pastProjectStatus ?? []),
          totalAllocatedHours: rest?.metrics?.totalAllocatedHours ?? 0,
          estimateAtCompletion: (rest?.metrics?.estimateAtCompletion ?? 0) * 60,
          estimateToComplete: (rest?.metrics?.estimateToComplete ?? 0) * 60,
          currency: rest?.projectFinancialsDetailModel?.currency,
          projectBudget: rest?.projectFinancialsDetailModel?.projectBudget,
          actualRevenue: rest?.metrics?.actualRevenue,
          actualCost: rest?.metrics?.actualCost,
          actualProfit: rest?.metrics?.actualProfit,
          actualProfitMargin: rest?.metrics?.actualProfitMargin,
          currentPeriodData: rest?.currentPeriodData,
          subscriptionFrequency:
            rest?.projectFinancialsDetailModel?.projectFinancialsMetaData
              ?.subscriptionFrequency,
          revenueRecognitionType:
            rest?.projectFinancialsDetailModel?.revenueRecognitionType ===
              CustomRevenueRecognitionTypes.CustomRevenueRecognition &&
            rest?.projectFinancialsDetailModel?.customRevenueRecognitionConfigId
              ? `${CustomRevenueRecognitionTypes.CustomRevenueRecognition}_${rest?.projectFinancialsDetailModel?.customRevenueRecognitionConfigId}`
              : rest?.projectFinancialsDetailModel?.revenueRecognitionType,
          financialContractType:
            rest?.projectFinancialsDetailModel?.financialContractType,
          currentPeriodNumber: rest?.currentPeriodData?.currentPeriodNumber,
          periodBudget:
            rest?.projectFinancialsDetailModel?.projectFinancialsMetaData
              ?.periodBudget,
          periodMinutes:
            rest.financialContractType ===
            ProjectFinancialsContractType.SUBSCRIPTION
              ? minsToHours(
                  rest.projectFinancialsDetailModel?.projectFinancialsMetaData
                    ?.periodMinutes ?? 0,
                )
              : 0,
          periodRevenue: rest?.currentPeriodData?.periodRevenue,
          periodTotalTimeTracked:
            rest?.currentPeriodData?.periodTotalTimeTracked,
          currentPeriodStartDate:
            rest?.currentPeriodData?.currentPeriodStartDate,
          currentPeriodEndDate: rest?.currentPeriodData?.currentPeriodEndDate,
        });

        return list;
      },
      initial,
    );
  };
